/**
 * SERVICE_FORMS — adaptive request-form modules per amenity.
 *
 * FIR-20260611-001 · ported from the reference build's vanilla-JS config.
 * Drives the visit-request modal: when a member selects an amenity chip,
 * the matching module is rendered with its fields.
 *
 * FIR-20260622-Timepicker · founder report 22 Jun 2026:
 *   Native `<input type="time">` shows an unfriendly 24-hour two-column
 *   picker (e.g. "23 23 / 00 24 / 01 25") that's especially confusing
 *   for non-engineering users. Replaced with curated 12-hour AM/PM
 *   dropdowns per activity. Slots stored as 24-hour `"HH:MM"` strings
 *   (backend wire-format unchanged) but rendered as `"7:00 AM"` etc.
 */

// Helper: build curated slot pairs `{ value: "HH:MM", label: "h:MM AM/PM" }`.
function makeSlots(startHour, endHour, stepMins = 30) {
  const out = [];
  for (let h = startHour; h <= endHour; h++) {
    for (let m = 0; m < 60; m += stepMins) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const value = `${hh}:${mm}`;
      const period = h < 12 ? "AM" : "PM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      const label = `${h12}:${mm} ${period}`;
      out.push({ value, label });
    }
  }
  return out;
}

// Curated slot ranges per activity (concierge-friendly defaults).
const DINING_SLOTS  = makeSlots(7, 23, 30);   // 7:00 AM – 11:30 PM
const TEE_SLOTS     = makeSlots(6, 17, 30);   // 6:00 AM – 5:30 PM (sunrise → late afternoon)
const SWIM_SLOTS    = makeSlots(6, 21, 30);   // 6:00 AM – 9:30 PM
const INDOOR_SLOTS  = makeSlots(8, 22, 30);   // 8:00 AM – 10:30 PM
const OUTDOOR_SLOTS = makeSlots(6, 21, 30);   // 6:00 AM – 9:30 PM
const FITNESS_SLOTS = makeSlots(6, 22, 30);   // 6:00 AM – 10:30 PM
const SPA_SLOTS     = makeSlots(9, 21, 30);   // 9:00 AM – 9:30 PM
const BAR_SLOTS     = makeSlots(12, 23, 30);  // 12:00 PM – 11:30 PM

export const SERVICE_FORMS = {
  Accommodation: {
    label: "Accommodation",
    icon: "🛏",
    fields: [
      { k: "checkin", l: "Check-in", t: "date", req: true },
      { k: "checkout", l: "Check-out", t: "date", req: true },
      { k: "rooms", l: "Rooms", t: "select", o: ["1", "2", "3", "4"] },
      { k: "guests", l: "Adults", t: "select", o: ["1", "2", "3", "4"] },
      { k: "notes", l: "Special requests", t: "textarea", full: true },
    ],
  },
  Dining: {
    label: "Dining",
    icon: "🍽",
    fields: [
      { k: "date", l: "Date", t: "date", req: true },
      { k: "time", l: "Time", t: "slot", o: DINING_SLOTS, req: true },
      { k: "meal", l: "Meal", t: "select", o: ["Breakfast", "Brunch", "Lunch", "High Tea", "Dinner"] },
      { k: "diet", l: "Dietary preference", t: "select", o: ["None", "Vegetarian", "Vegan", "Jain", "Halal", "Gluten-free"] },
      { k: "notes", l: "Occasion / notes", t: "textarea", full: true },
    ],
  },
  Golf: {
    label: "Golf",
    icon: "⛳",
    fields: [
      { k: "date", l: "Date", t: "date", req: true },
      { k: "tee", l: "Preferred tee time", t: "slot", o: TEE_SLOTS },
      { k: "players", l: "Players", t: "select", o: ["1", "2", "3", "4"] },
      { k: "hire", l: "Equipment hire", t: "select", o: ["Not required", "Clubs", "Clubs + cart"] },
      { k: "caddie", l: "Caddie", t: "select", o: ["Not required", "Yes"] },
    ],
  },
  Swimming: {
    label: "Swimming",
    icon: "🏊",
    fields: [
      { k: "date", l: "Date", t: "date", req: true },
      { k: "time", l: "Time", t: "slot", o: SWIM_SLOTS },
      { k: "notes", l: "Notes", t: "textarea", full: true },
    ],
  },
  "Indoor Games": {
    label: "Indoor games",
    icon: "🎱",
    fields: [
      { k: "date", l: "Date", t: "date", req: true },
      { k: "time", l: "Time", t: "slot", o: INDOOR_SLOTS },
      { k: "game", l: "Game", t: "select", o: ["Billiards / Snooker", "Table Tennis", "Cards", "Carrom", "Chess", "Other"] },
      { k: "notes", l: "Notes", t: "textarea", full: true },
    ],
  },
  "Outdoor Games": {
    label: "Outdoor games",
    icon: "🎾",
    fields: [
      { k: "date", l: "Date", t: "date", req: true },
      { k: "time", l: "Time", t: "slot", o: OUTDOOR_SLOTS },
      { k: "activity", l: "Activity", t: "select", o: ["Tennis", "Squash", "Badminton", "Cricket", "Other"] },
      { k: "notes", l: "Notes", t: "textarea", full: true },
    ],
  },
  "Sports & Fitness": {
    label: "Sports & fitness",
    icon: "💪",
    fields: [
      { k: "date", l: "Date", t: "date", req: true },
      { k: "time", l: "Time", t: "slot", o: FITNESS_SLOTS },
      { k: "facility", l: "Facility", t: "select", o: ["Gymnasium", "Fitness class", "Court", "Track"] },
      { k: "notes", l: "Notes", t: "textarea", full: true },
    ],
  },
  "Health & Wellness": {
    label: "Health & wellness",
    icon: "🌿",
    fields: [
      { k: "date", l: "Date", t: "date", req: true },
      { k: "time", l: "Time", t: "slot", o: SPA_SLOTS },
      { k: "service", l: "Service", t: "select", o: ["Spa", "Salon", "Massage", "Steam & Sauna", "Wellness consult"] },
      { k: "notes", l: "Notes", t: "textarea", full: true },
    ],
  },
  Bar: {
    label: "Bar / Lounge",
    icon: "🥂",
    fields: [
      { k: "date", l: "Date", t: "date", req: true },
      { k: "time", l: "Time", t: "slot", o: BAR_SLOTS },
      { k: "notes", l: "Notes", t: "textarea", full: true },
    ],
  },
};

export const AMENITIES = [
  "Golf",
  "Dining",
  "Accommodation",
  "Bar",
  "Swimming",
  "Indoor Games",
  "Outdoor Games",
  "Sports & Fitness",
  "Health & Wellness",
  "Sailing",
  "Beach",
];

export const TYPES = [
  "City & Social",
  "Gymkhana",
  "Golf",
  "Sports & Recreation",
  "Country Club",
  "Cosmopolitan",
  "Turf & Equestrian",
  "Yacht & Sailing",
  "Beach Club",
  "Accommodation",
  "Adventure & Outdoor Club",
  "Art & Design Club",
  "Business, Founder & Innovation Club",
  "Dining",
  "Equestrian Club",
  "Fishing Club",
  "Flying Club",
  "Food & Wine Club",
  "Motorsport & Driving Club",
  "Photography Club",
  "Racquet & Social Sports Club",
  "Sailing Club",
  "Shooting Club",
  "Wellness, Bathhouse & Recovery Club",
  "Yacht Club",
];
